import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { AppStoreProvider } from './stores/application/useAppStore';
import { AuthStoreProvider } from './stores/auth/useAuthStore';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ConnectionsStoreProvider } from './stores/connections/useConnectionsStore';
import { TagsStoreProvider } from './stores/tags/useTagsStore';
import { TasksStoreProvider } from './stores/tasks/useTasksStore';
import { NotesStoreProvider } from './stores/notes/useNotesStore';
import { TemplateStoreProvider } from './stores/templates/useTemplateStore';
import { WorkspaceUsersStoreProvider } from './stores/workspaceUsers/useWorkspaceUsersStore';
import { HubspotStoreProvider } from './stores/hubspot/useHubspotStore';
import { AssistantStoreProvider } from './stores/assistant/useAssistantStore';
import { FeedsStoreProvider } from './stores/feeds/useFeedsStore';
import { InboxSettingsStoreProvider } from './stores/inbox/useInboxSettingsStore';
import { PostHogProvider } from 'posthog-js/react'
//import reportWebVitals from './reportWebVitals';

/**
 * The mailbox consent popup, closing itself.
 *
 * The provider redirects back INSIDE the 520px window it opened. Nothing there is for
 * the user — the window that opened it is already polling for the mailbox — so it
 * closes rather than rendering the whole of LeadDelta in a sliver.
 *
 * This has to run before React does. The check used to live in the senders component,
 * which never mounted here: the popup lands on the campaign wizard at step 1, and that
 * component belongs to a later step. So the flag arrived and nothing acted on it.
 *
 * Two conditions are required — the return flag and an opener — because closing the
 * wrong window would be far worse than failing to close this one. Together they are
 * specific enough: only the consent round-trip lands on a URL carrying that flag in a
 * window something else opened.
 *
 * The window name is used only to REJECT, never to require. Chrome clears window.name
 * on cross-origin navigation, and this window has been to the provider and back — so
 * an empty name is expected and must not veto the close. A name that is present and
 * belongs to something else is a genuine signal to leave the window alone.
 */
function closeMailboxConsentPopup(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('mailbox') !== 'connected') return false;
    if (!window.opener || window.opener === window) return false;
    if (window.name && !window.name.startsWith('ld-mail')) return false;
    window.close();
    return true;
  } catch {
    // Cross-origin opener access can throw; rendering the app is the safe fallback.
    return false;
  }
}

const isConsentPopup = closeMailboxConsentPopup();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const queryClient = new QueryClient();

// Global error handler for Google Translate DOM conflicts
// Google Translate modifies the DOM and can cause insertBefore/removeChild errors
// when libraries try to manipulate nodes that Google Translate has moved
const setupGoogleTranslateProtection = () => {
  // Wrap Node.prototype.insertBefore to catch and handle errors
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function<T extends Node>(newNode: T, referenceNode: Node | null): T {
    try {
      // Check if reference node is still a child of this node
      if (referenceNode && !this.contains(referenceNode)) {
        console.debug('Reference node no longer a child, using appendChild instead');
        return this.appendChild(newNode);
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    } catch (error) {
      // If insertBefore fails (often due to Google Translate), fallback to appendChild
      if (error instanceof Error && error.name === 'NotFoundError') {
        console.debug('insertBefore failed (likely Google Translate), using appendChild:', error.message);
        try {
          return this.appendChild(newNode);
        } catch (appendError) {
          console.debug('appendChild also failed:', appendError);
          // Return the node itself as a last resort
          return newNode;
        }
      }
      throw error;
    }
  };

  // Wrap Node.prototype.removeChild to catch and handle errors
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function<T extends Node>(child: T): T {
    try {
      // Check if child is still a child of this node
      if (!this.contains(child)) {
        console.debug('Child node no longer a child of parent, skipping removeChild');
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    } catch (error) {
      // If removeChild fails (often due to Google Translate), silently ignore
      if (error instanceof Error && error.name === 'NotFoundError') {
        console.debug('removeChild failed (likely Google Translate), child already removed or moved:', error.message);
        // Return the child node even though it wasn't removed (it's already gone)
        return child;
      }
      throw error;
    }
  };
};

// Set up protection before React renders
setupGoogleTranslateProtection();

const options = {
  api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || ''
}

/*
  window.close() is asynchronous — the window is still alive for a tick — so skip the
  render rather than briefly mounting the whole app inside the popup and firing every
  store's startup request on the way out.
*/
if (!isConsentPopup) root.render(
  <QueryClientProvider client={queryClient}>
    <AppStoreProvider>
      <AuthStoreProvider>
        <AssistantStoreProvider>
          <TemplateStoreProvider>
            <NotesStoreProvider>
              <TasksStoreProvider>
                <WorkspaceUsersStoreProvider>
                  <HubspotStoreProvider>
                    <ConnectionsStoreProvider>
                      <TagsStoreProvider>
                        <InboxSettingsStoreProvider>
                          <FeedsStoreProvider>
                            <PostHogProvider apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY || ''} options={options}>
                              <App />
                            </PostHogProvider>
                          </FeedsStoreProvider>
                        </InboxSettingsStoreProvider>
                      </TagsStoreProvider>
                    </ConnectionsStoreProvider>
                  </HubspotStoreProvider>
                </WorkspaceUsersStoreProvider>
              </TasksStoreProvider>
            </NotesStoreProvider>
          </TemplateStoreProvider>
        </AssistantStoreProvider>
      </AuthStoreProvider>
    </AppStoreProvider>
  </QueryClientProvider>
);

/* // If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(); */
